import { Chip } from "@ui/Atoms/Chip/Chip";
import { Toggle } from "@ui/Atoms/Toggle/Toggle";
import { IconClock } from "@ui/Icons/IconClock";
import { FilterGroup } from "@ui/Molecules/Filter/FilterGroup";
import { useState } from "react";
import type { FilterHandlers, FilterSection } from "./types";

type Props = {
	sections: FilterSection[];
	handlers: FilterHandlers;
	chipSize?: "sm" | "md";
	/** Chips shown before a "show more" toggle appears. */
	chipLimit?: number;
};

function Segment({
	choices,
	onSelect,
}: {
	choices: { value: string; label: string; selected: boolean }[];
	onSelect: (value: string) => void;
}) {
	return (
		<>
			{choices.map((choice) => (
				<button
					key={choice.value}
					type="button"
					onClick={() => onSelect(choice.value)}
					className="flex h-10 flex-1 items-center justify-center rounded-[var(--radius-sm)] text-[13px] font-bold"
					style={{
						background: choice.selected
							? "var(--surface-inverted)"
							: "var(--surface-default)",
						color: choice.selected
							? "var(--content-inverted)"
							: "var(--content-primary)",
						border: `1px solid ${choice.selected ? "var(--surface-inverted)" : "var(--border-strong)"}`,
					}}
				>
					{choice.label}
				</button>
			))}
		</>
	);
}

type ChipChoice = {
	slug: string;
	label: string;
	count?: number;
	selected: boolean;
};

function ChipSection({
	section,
	choices,
	chipSize,
	limit,
	onToggle,
}: {
	section: Extract<FilterSection, { kind: "chips" }>;
	choices: ChipChoice[];
	chipSize: "sm" | "md";
	limit: number;
	onToggle: (slug: string) => void;
}) {
	const [expanded, setExpanded] = useState(false);
	const overflow = choices.length - limit;

	const visible = expanded
		? choices
		: (() => {
				const selected = choices.filter((c) => c.selected);
				const rest = choices.filter((c) => !c.selected);
				return [...selected, ...rest].slice(
					0,
					Math.max(limit, selected.length),
				);
			})();

	return (
		<FilterGroup
			label={section.label}
			action={
				section.action ? (
					<button
						type="button"
						onClick={section.action.onClick}
						className="text-[12.5px] font-bold text-[var(--accent-500)]"
					>
						{section.action.label}
					</button>
				) : section.hint ? (
					<span className="text-[12px] font-semibold text-[var(--content-faint)]">
						{section.hint}
					</span>
				) : undefined
			}
		>
			{visible.map((choice) => (
				<Chip
					key={choice.slug}
					size={chipSize}
					label={choice.label}
					count={choice.count}
					selected={choice.selected}
					onClick={() => onToggle(choice.slug)}
				/>
			))}
			{overflow > 0 && (
				<button
					type="button"
					onClick={() => setExpanded((v) => !v)}
					className="inline-flex items-center rounded-[var(--radius-pill)] border border-dashed border-[var(--border-strong)] px-3 py-2 text-[12.5px] font-bold text-[var(--content-muted)]"
				>
					{expanded ? "Minder" : `Toon alle (${choices.length})`}
				</button>
			)}
		</FilterGroup>
	);
}

export function FilterSections({
	sections,
	handlers,
	chipSize = "sm",
	chipLimit = 10,
}: Props) {
	return (
		<>
			{sections.map((section) => {
				if (section.kind === "recent") {
					return (
						<FilterGroup key={section.id} label={section.label}>
							<Chip
								variant="dashed"
								size={chipSize}
								label={section.combo}
								icon={<IconClock size={13} color="var(--content-faint)" />}
								onClick={() => handlers.onApplyRecent(section.id)}
							/>
						</FilterGroup>
					);
				}

				if (section.kind === "toggle") {
					return (
						<FilterGroup key={section.id} label={section.label}>
							<Toggle
								checked={section.value}
								onChange={() => handlers.onToggleSwitch(section.id)}
								label={section.valueLabel}
							/>
						</FilterGroup>
					);
				}

				if (section.kind === "segment") {
					return (
						<FilterGroup
							key={section.id}
							label={section.label}
							layout="row"
							action={
								section.valueLabel ? (
									<span className="text-[12.5px] font-bold text-[var(--content-primary)]">
										{section.valueLabel}
									</span>
								) : undefined
							}
						>
							<Segment
								choices={section.choices}
								onSelect={(value) =>
									handlers.onSelectSegment(section.id, value)
								}
							/>
						</FilterGroup>
					);
				}

				return (
					<ChipSection
						key={section.id}
						section={section}
						choices={section.choices}
						chipSize={chipSize}
						limit={chipLimit}
						onToggle={(slug) => handlers.onToggleChoice(section.id, slug)}
					/>
				);
			})}
		</>
	);
}
